import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import authService from '../services/auth.service';
import { useNavigate } from 'react-router-dom';
import './TodoTemplates.css';

const TodoTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [showInactiveTemplates, setShowInactiveTemplates] = useState(true);
  const [newTemplate, setNewTemplate] = useState({
    title: '',
    description: '',
    is_active: true,
    items: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activationConfirm, setActivationConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [removeItemConfirm, setRemoveItemConfirm] = useState(null); // { mode: 'new'|'edit', index }

  const groupIssuesByIndex = (issues) => {
    const map = new Map();
    for (const issue of issues) {
      const list = map.get(issue.index) || [];
      list.push(issue);
      map.set(issue.index, list);
    }
    return map;
  };

  const hasAnyError = (issues) => issues.some((issue) => issue.severity === 'error');

  const normalizeItems = (rawItems) => {
    const itemsArray = Array.isArray(rawItems) ? rawItems : [];
    const normalized = [];

    for (const raw of itemsArray) {
      const title = (raw?.title || '').trim();
      const description = (raw?.description || '').trim();
      const requires_image = !!raw?.requires_image;

      const hasAnyInput = Boolean(title || description || requires_image);
      if (!hasAnyInput) continue;

      if (!title) {
        return { error: 'Madde başlığı boş olamaz. Boş satırları kaldırın.' };
      }

      const item = {
        title,
        description: description || null,
        requires_image
      };

      if (raw?.id) item.id = raw.id;
      normalized.push(item);
    }

    return { items: normalized };
  };

  const getItemIssues = (rawItems) => {
    const itemsArray = Array.isArray(rawItems) ? rawItems : [];
    const issues = [];

    const titleCounts = new Map();
    itemsArray.forEach((raw) => {
      const title = (raw?.title || '').trim().toLocaleLowerCase('tr');
      if (!title) return;
      titleCounts.set(title, (titleCounts.get(title) || 0) + 1);
    });

    itemsArray.forEach((raw, index) => {
      const title = (raw?.title || '').trim();
      const description = (raw?.description || '').trim();
      const requires_image = !!raw?.requires_image;
      const hasAnyInput = Boolean(title || description || requires_image);

      if (hasAnyInput && !title) {
        issues.push({ index, severity: 'error', message: 'Madde başlığı zorunlu.' });
        return;
      }

      const normalizedTitle = title.trim().toLocaleLowerCase('tr');
      if (normalizedTitle && (titleCounts.get(normalizedTitle) || 0) > 1) {
        issues.push({ index, severity: 'warning', message: 'Aynı başlık birden fazla kez eklenmiş.' });
      }
    });

    return issues;
  };

  const moveItem = (items, from, to) => {
    if (to < 0 || to >= items.length) return items;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  };

  const duplicateItem = (items, index) => {
    const next = [...items];
    const copy = { ...next[index] };
    delete copy.id;
    next.splice(index + 1, 0, copy);
    return next;
  };

  const removeItemAt = (items, index) => {
    const next = [...items];
    next.splice(index, 1);
    return next;
  };

  const cleanupItems = (items) => {
    const itemsArray = Array.isArray(items) ? items : [];
    return itemsArray
      .map((raw) => {
        const next = {
          ...raw,
          title: (raw?.title || '').trim(),
          description: (raw?.description || '').trim(),
          requires_image: !!raw?.requires_image
        };
        if (!next.description) next.description = '';
        return next;
      })
      .filter((raw) => Boolean(raw.title || raw.description || raw.requires_image));
  };

  // Şablonları yükle
  useEffect(() => {
    // JWT token kontrolü
    if (!authService.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    
    fetchTemplates();
  }, [navigate]);

  // Tüm şablonları getir
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/api/todo/templates');
      setTemplates(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Şablonlar yüklenemedi');
      console.error('Şablonları getirme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  // Şablonu detaylarıyla getir
  const fetchTemplateDetails = async (templateId) => {
    setLoading(true);
    try {
      const response = await apiService.get(`/api/todo/templates/${templateId}`);
      // Boş items dizisi kontrolü
      const templateData = {
        ...response.data,
        items: response.data.items || []
      };
      setEditingTemplate(templateData);
    } catch (err) {
      setError(err.response?.data?.error || 'Şablon detayları yüklenemedi');
      console.error('Şablon detayları getirme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  // Şablon oluştur
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!newTemplate.title.trim()) {
      setError('Şablon başlığı gereklidir');
      return;
    }

    const normalized = normalizeItems(newTemplate.items);
    if (normalized.error) {
      setError(normalized.error);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        title: newTemplate.title.trim(),
        description: (newTemplate.description || '').trim() || null,
        is_active: !!newTemplate.is_active,
        items: normalized.items || []
      };

      const response = await apiService.post('/api/todo/templates', payload);
      
      // Başarılı
      setTemplates([...templates, response.data]);
      setNewTemplate({ 
        title: '', 
        description: '', 
        is_active: true,
        items: [] 
      });
      setSuccess('Şablon başarıyla oluşturuldu');

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Şablon oluşturulamadı');
      console.error('Şablon oluşturma hatası:', err);
      
      // Token geçersiz ise login sayfasına yönlendir
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Şablon güncelle
  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    if (!editingTemplate.title.trim()) {
      setError('Şablon başlığı gereklidir');
      return;
    }

    const normalized = normalizeItems(editingTemplate.items);
    if (normalized.error) {
      setError(normalized.error);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...editingTemplate,
        title: editingTemplate.title.trim(),
        description: (editingTemplate.description || '').trim() || null,
        items: normalized.items || []
      };

      const response = await apiService.put(`/api/todo/templates/${editingTemplate.id}`, payload);

      // Başarılı
      const updatedTemplate = response.data;
      setTemplates((prev) => {
        // Normal güncelleme: aynı ID
        if (prev.some((t) => t.id === updatedTemplate.id)) {
          return prev.map((t) => (t.id === updatedTemplate.id ? updatedTemplate : t));
        }

        // Sürümleme: eski şablon pasifleştirilip yeni ID ile dönebilir
        const withoutOld = prev.filter((t) => t.id !== editingTemplate.id);
        return [...withoutOld, updatedTemplate].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'tr'));
      });
      setEditingTemplate(null);
      setSuccess('Şablon başarıyla güncellendi');

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Şablon güncellenemedi');
      console.error('Şablon güncelleme hatası:', err);
      
      // Token geçersiz ise login sayfasına yönlendir
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Şablon silmeyi başlat - onay göster
  const startDeleteTemplate = (templateId) => {
    setDeleteConfirm(templateId);
  };

  // Şablon silmeyi iptal et
  const cancelDeleteTemplate = () => {
    setDeleteConfirm(null);
  };

  // Şablon sil
  const handleDeleteTemplate = async (id) => {
    setDeleteConfirm(null);
    setLoading(true);
    setError(null);

    try {
      await apiService.delete(`/api/todo/templates/${id}`);

      // Başarılı
      setTemplates(templates.filter(t => t.id !== id));
      setSuccess('Şablon başarıyla silindi');

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Şablon silinemedi');
      console.error('Şablon silme hatası:', err);
      
      // Token geçersiz ise login sayfasına yönlendir
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Şablon aktivasyon durumunu değiştir
  const handleToggleActivation = async (templateId, isActive) => {
    // Önceki onaylamayı temizle
    setActivationConfirm(null);
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.patch(`/api/todo/templates/${templateId}/activate`, {
        is_active: isActive
      });

      // Başarılı
      const updatedTemplate = response.data;
      setTemplates(templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t));
      
      setSuccess(`Şablon ${isActive ? 'aktif' : 'pasif'} duruma getirildi`);

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || `Şablon ${isActive ? 'aktif' : 'pasif'} duruma getirilemedi`);
      console.error('Şablon aktivasyon hatası:', err);
      
      // Token geçersiz ise login sayfasına yönlendir
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Yeni şablon maddeleri ekle/kaldır
  const handleAddItem = () => {
    setNewTemplate({
      ...newTemplate,
      items: [
        ...newTemplate.items,
        { title: '', description: '', requires_image: false }
      ]
    });
  };

  const handleRemoveItem = (index) => {
    setRemoveItemConfirm({ mode: 'new', index });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...newTemplate.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setNewTemplate({ ...newTemplate, items: updatedItems });
  };

  // Düzenleme modu için madde ekle/kaldır
  const handleEditAddItem = () => {
    setEditingTemplate({
      ...editingTemplate,
      items: [
        ...(editingTemplate.items || []),
        { title: '', description: '', requires_image: false }
      ]
    });
  };

  const handleEditRemoveItem = (index) => {
    setRemoveItemConfirm({ mode: 'edit', index });
  };

  const confirmRemoveItem = () => {
    if (!removeItemConfirm) return;

    if (removeItemConfirm.mode === 'new') {
      setNewTemplate((prev) => ({ ...prev, items: removeItemAt(prev.items || [], removeItemConfirm.index) }));
    } else {
      setEditingTemplate((prev) => {
        if (!prev) return prev;
        return { ...prev, items: removeItemAt(prev.items || [], removeItemConfirm.index) };
      });
    }

    setRemoveItemConfirm(null);
  };

  const cancelRemoveItem = () => setRemoveItemConfirm(null);

  const handleEditItemChange = (index, field, value) => {
    const updatedItems = [...(editingTemplate.items || [])];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setEditingTemplate({ ...editingTemplate, items: updatedItems });
  };

  // Düzenleme moduna geç
  const handleStartEditing = (template) => {
    // Şablon detaylarını API'den getir (items'ları yüklemek için)
    fetchTemplateDetails(template.id);
  };

  const activeCount = templates.filter((t) => t.is_active).length;
  const inactiveCount = templates.length - activeCount;
  const baseVisibleTemplates = templates.filter((t) => (showInactiveTemplates ? true : t.is_active));
  const searchText = templateSearch.trim().toLocaleLowerCase('tr');
  const filteredTemplates = baseVisibleTemplates
    .filter((t) => {
      if (!searchText) return true;
      const haystack = `${t.title || ''} ${t.description || ''}`.toLocaleLowerCase('tr');
      return haystack.includes(searchText);
    })
    .sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return (a.title || '').localeCompare(b.title || '', 'tr');
    });

  const newItemIssues = getItemIssues(newTemplate.items);
  const newItemIssuesByIndex = groupIssuesByIndex(newItemIssues);
  const newHasErrors = hasAnyError(newItemIssues);

  const editItemIssues = getItemIssues(editingTemplate?.items || []);
  const editItemIssuesByIndex = groupIssuesByIndex(editItemIssues);
  const editHasErrors = hasAnyError(editItemIssues);

  return (
    <div className="todo-templates">
      <div className="page-header">
        <div className="page-title">
          <h1>Yapılacaklar Şablonları</h1>
          <p className="page-subtitle">
            Aktif şablonlar gün açıldığında otomatik görünür. Pasif yapmak mevcut gün kayıtlarını silmez; sadece yeni günlerde otomatik eklenmesini durdurur.
          </p>
        </div>
      </div>
      
      {/* Mesajlar */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Madde silme onayı */}
      {removeItemConfirm && (
        <div className="modal-backdrop" onClick={cancelRemoveItem} role="presentation">
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Maddeyi kaldır?</h3>
            </div>
            <div className="modal-body">
              <p>Bu madde formdan kaldırılacak.</p>
              {removeItemConfirm.mode === 'edit' && (
                <p className="modal-hint">
                  Not: Var olan bir maddeyi kaldırıp kaydederseniz sistem mevcut kayıtları bozmamak için yeni bir şablon sürümü oluşturabilir.
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-danger" onClick={confirmRemoveItem}>
                Kaldır
              </button>
              <button type="button" className="btn btn-secondary" onClick={cancelRemoveItem}>
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Yeni şablon oluşturma formu */}
      <div className="template-grid">
        <div className="template-form-container panel">
          <div className="panel-header">
            <h2>Yeni Şablon</h2>
            <p className="panel-subtitle">Sık kullanılan işleri şablonlayın; gün açıldığında otomatik gelsin.</p>
          </div>
          <form onSubmit={handleCreateTemplate}>
            <div className="form-group">
              <label>Başlık</label>
              <input
                type="text"
                value={newTemplate.title}
                onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
                className="form-control"
                placeholder="Örn: Açılış Kontrol Listesi"
                required
              />
            </div>

            <div className="form-group">
              <label>Açıklama</label>
              <textarea
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                className="form-control"
                placeholder="Kısa bir not (opsiyonel)"
              ></textarea>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={newTemplate.is_active}
                  onChange={(e) => setNewTemplate({ ...newTemplate, is_active: e.target.checked })}
                />
                Aktif (Gün açıldığında otomatik görünür)
              </label>
              <div className="field-hint">Pasif yaparsanız mevcut gün kayıtları korunur.</div>
            </div>

            <div className="items-header">
              <h3>Şablon Maddeleri</h3>
              <div className="items-header-actions">
                {newTemplate.items.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setNewTemplate((prev) => ({ ...prev, items: cleanupItems(prev.items || []) }))}
                  >
                    Boş satırları temizle
                  </button>
                )}
                <button type="button" onClick={handleAddItem} className="btn btn-secondary">
                  + Madde Ekle
                </button>
              </div>
            </div>

            {newTemplate.items.length === 0 ? (
              <div className="empty-items">
                Henüz madde yok. <button type="button" className="link-button" onClick={handleAddItem}>İlk maddeyi ekleyin</button>.
              </div>
            ) : (
              newTemplate.items.map((item, index) => {
                const issuesForItem = newItemIssuesByIndex.get(index) || [];
                const hasError = issuesForItem.some((i) => i.severity === 'error');
                const hasWarning = !hasError && issuesForItem.some((i) => i.severity === 'warning');

                return (
                  <div key={index} className="template-item item-card">
                    <div className="item-top">
                      <div className="item-index">#{index + 1}</div>
                      <div className="item-actions">
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setNewTemplate((prev) => ({ ...prev, items: moveItem(prev.items || [], index, index - 1) }))}
                          disabled={index === 0}
                          aria-label="Yukarı taşı"
                          title="Yukarı taşı"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setNewTemplate((prev) => ({ ...prev, items: moveItem(prev.items || [], index, index + 1) }))}
                          disabled={index === newTemplate.items.length - 1}
                          aria-label="Aşağı taşı"
                          title="Aşağı taşı"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setNewTemplate((prev) => ({ ...prev, items: duplicateItem(prev.items || [], index) }))}
                          aria-label="Kopyala"
                          title="Kopyala"
                        >
                          Kopyala
                        </button>
                        <button
                          type="button"
                          className="icon-button danger"
                          onClick={() => handleRemoveItem(index)}
                          aria-label="Kaldır"
                          title="Kaldır"
                        >
                          Sil
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Madde Başlığı</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                        className={`form-control ${hasError ? 'has-error' : hasWarning ? 'has-warning' : ''}`}
                        placeholder="Örn: Vitrin kontrolü"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Madde Açıklaması</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        className="form-control"
                        placeholder="Detay (opsiyonel)"
                      ></textarea>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={item.requires_image}
                          onChange={(e) => handleItemChange(index, 'requires_image', e.target.checked)}
                        />
                        Görsel gerekiyor
                      </label>
                    </div>

                    {issuesForItem.length > 0 && (
                      <div className="item-issues">
                        {issuesForItem.map((issue, i) => (
                          <div key={i} className={`issue ${issue.severity}`}>
                            {issue.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !newTemplate.title.trim() || newHasErrors}
              >
                {loading ? 'Oluşturuluyor...' : 'Şablon Oluştur'}
              </button>
              {newHasErrors && <div className="inline-hint">Kaydetmek için maddelerdeki hataları düzeltin.</div>}
            </div>
          </form>
        </div>
      
      {/* Şablon listesi */}
        <div className="templates-list panel">
          <div className="panel-header">
            <div className="panel-header-row">
              <h2>Mevcut Şablonlar</h2>
              <div className="counts">
                <span className="count-pill">{activeCount} aktif</span>
                <span className="count-pill muted">{inactiveCount} pasif</span>
              </div>
            </div>
            <div className="templates-toolbar">
              <div className="search">
                <input
                  type="text"
                  className="form-control"
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  placeholder="Şablon ara (başlık / açıklama)"
                />
              </div>
              <label className="toolbar-toggle">
                <input
                  type="checkbox"
                  checked={showInactiveTemplates}
                  onChange={(e) => setShowInactiveTemplates(e.target.checked)}
                />
                Pasifleri göster
              </label>
            </div>
          </div>

          {loading && !editingTemplate ? (
            <div className="loading">Yükleniyor...</div>
          ) : templates.length === 0 ? (
            <div className="no-templates">Henüz şablon bulunmamaktadır.</div>
          ) : filteredTemplates.length === 0 ? (
            <div className="no-templates">
              {templateSearch.trim()
                ? 'Arama kriterinize uygun şablon bulunamadı.'
                : !showInactiveTemplates && activeCount === 0
                  ? 'Aktif şablon yok. Pasifleri göster seçeneğini açın.'
                  : 'Gösterilecek şablon yok.'}
            </div>
          ) : (
            filteredTemplates.map((template) => (
              <div key={template.id} className="template-card">
                {editingTemplate && editingTemplate.id === template.id ? (
                  // Düzenleme formu
                  <form onSubmit={handleUpdateTemplate}>
                    <div className="form-group">
                      <label>Başlık</label>
                      <input
                        type="text"
                        value={editingTemplate.title}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, title: e.target.value })}
                        className="form-control"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Açıklama</label>
                      <textarea
                        value={editingTemplate.description || ''}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                        className="form-control"
                      ></textarea>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={editingTemplate.is_active}
                          onChange={(e) => setEditingTemplate({ ...editingTemplate, is_active: e.target.checked })}
                        />
                        Aktif
                      </label>
                      <div className="field-hint">Değişiklikler kaydedildiğinde geçerli olur.</div>
                    </div>

                    <div className="items-header">
                      <h3>Şablon Maddeleri</h3>
                      <div className="items-header-actions">
                        {(editingTemplate.items || []).length > 0 && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() =>
                              setEditingTemplate((prev) => {
                                if (!prev) return prev;
                                return { ...prev, items: cleanupItems(prev.items || []) };
                              })
                            }
                          >
                            Boş satırları temizle
                          </button>
                        )}
                        <button type="button" onClick={handleEditAddItem} className="btn btn-secondary">
                          + Madde Ekle
                        </button>
                      </div>
                    </div>

                    {(editingTemplate.items || []).length === 0 ? (
                      <div className="empty-items">
                        Henüz madde yok. <button type="button" className="link-button" onClick={handleEditAddItem}>İlk maddeyi ekleyin</button>.
                      </div>
                    ) : (
                      (editingTemplate.items || []).map((item, index) => {
                        const issuesForItem = editItemIssuesByIndex.get(index) || [];
                        const hasError = issuesForItem.some((i) => i.severity === 'error');
                        const hasWarning = !hasError && issuesForItem.some((i) => i.severity === 'warning');

                        return (
                          <div key={index} className="template-item item-card">
                            <div className="item-top">
                              <div className="item-index">#{index + 1}</div>
                              <div className="item-actions">
                                <button
                                  type="button"
                                  className="icon-button"
                                  onClick={() =>
                                    setEditingTemplate((prev) => {
                                      if (!prev) return prev;
                                      return { ...prev, items: moveItem(prev.items || [], index, index - 1) };
                                    })
                                  }
                                  disabled={index === 0}
                                  aria-label="Yukarı taşı"
                                  title="Yukarı taşı"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="icon-button"
                                  onClick={() =>
                                    setEditingTemplate((prev) => {
                                      if (!prev) return prev;
                                      return { ...prev, items: moveItem(prev.items || [], index, index + 1) };
                                    })
                                  }
                                  disabled={index === (editingTemplate.items || []).length - 1}
                                  aria-label="Aşağı taşı"
                                  title="Aşağı taşı"
                                >
                                  ↓
                                </button>
                                <button
                                  type="button"
                                  className="icon-button"
                                  onClick={() =>
                                    setEditingTemplate((prev) => {
                                      if (!prev) return prev;
                                      return { ...prev, items: duplicateItem(prev.items || [], index) };
                                    })
                                  }
                                  aria-label="Kopyala"
                                  title="Kopyala"
                                >
                                  Kopyala
                                </button>
                                <button
                                  type="button"
                                  className="icon-button danger"
                                  onClick={() => handleEditRemoveItem(index)}
                                  aria-label="Kaldır"
                                  title="Kaldır"
                                >
                                  Sil
                                </button>
                              </div>
                            </div>

                            <div className="form-group">
                              <label>Madde Başlığı</label>
                              <input
                                type="text"
                                value={item.title}
                                onChange={(e) => handleEditItemChange(index, 'title', e.target.value)}
                                className={`form-control ${hasError ? 'has-error' : hasWarning ? 'has-warning' : ''}`}
                                required
                              />
                            </div>

                            <div className="form-group">
                              <label>Madde Açıklaması</label>
                              <textarea
                                value={item.description || ''}
                                onChange={(e) => handleEditItemChange(index, 'description', e.target.value)}
                                className="form-control"
                              ></textarea>
                            </div>

                            <div className="form-group checkbox-group">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={item.requires_image}
                                  onChange={(e) => handleEditItemChange(index, 'requires_image', e.target.checked)}
                                />
                                Görsel gerekiyor
                              </label>
                            </div>

                            {issuesForItem.length > 0 && (
                              <div className="item-issues">
                                {issuesForItem.map((issue, i) => (
                                  <div key={i} className={`issue ${issue.severity}`}>
                                    {issue.message}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}

                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary" disabled={loading || editHasErrors}>
                        {loading ? 'Güncelleniyor...' : 'Kaydet'}
                      </button>
                      <button type="button" onClick={() => setEditingTemplate(null)} className="btn btn-secondary">
                        İptal
                      </button>
                      {editHasErrors && <div className="inline-hint">Kaydetmek için maddelerdeki hataları düzeltin.</div>}
                    </div>
                  </form>
                ) : (
                  // Şablon görünümü
                  <>
                    <div className="template-header">
                      <div className="template-title">
                        <h3>{template.title}</h3>
                        {template.description && <p className="template-description">{template.description}</p>}
                      </div>
                      <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                        {template.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>

                    <div className="template-actions">
                      <div className="button-row">
                        <button onClick={() => handleStartEditing(template)} className="btn btn-secondary" disabled={loading}>
                          Düzenle
                        </button>

                        {deleteConfirm === template.id ? (
                          <button className="btn btn-danger" onClick={() => handleDeleteTemplate(template.id)} disabled={loading}>
                            Evet, Sil
                          </button>
                        ) : (
                          <button onClick={() => startDeleteTemplate(template.id)} className="btn btn-danger" disabled={loading}>
                            Sil
                          </button>
                        )}

                        {activationConfirm === template.id ? (
                          <button
                            className={`btn ${template.is_active ? 'btn-secondary' : 'btn-primary'}`}
                            onClick={() => handleToggleActivation(template.id, !template.is_active)}
                            disabled={loading}
                          >
                            Evet, {template.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                          </button>
                        ) : (
                          <button
                            onClick={() => setActivationConfirm(template.id)}
                            className={`btn ${template.is_active ? 'btn-secondary' : 'btn-primary'}`}
                            disabled={loading}
                          >
                            {template.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                          </button>
                        )}
                      </div>

                      {deleteConfirm === template.id && (
                        <div className="inline-confirm">
                          <div className="inline-confirm-text">
                            Bu şablon tamamen silinir. Geri alınamaz.
                          </div>
                          <button type="button" className="btn btn-ghost" onClick={cancelDeleteTemplate}>
                            İptal
                          </button>
                        </div>
                      )}

                      {activationConfirm === template.id && (
                        <div className="inline-confirm">
                          <div className="inline-confirm-text">
                            {template.is_active
                              ? 'Pasif yaparsanız yeni günlerde otomatik eklenmez; mevcut kayıtlar korunur.'
                              : 'Aktif yaparsanız gün açıldığında otomatik görünür. Geçmiş günlere otomatik eklenmez.'}
                          </div>
                          <button type="button" className="btn btn-ghost" onClick={() => setActivationConfirm(null)}>
                            İptal
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TodoTemplates;
